import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

interface TitleLayer {
  id: number;
  title: string;
  loading: boolean;
}

interface PageTitleContextValue {
  title: string;
  titleLoading: boolean;
  pushTitle: (title: string, loading: boolean) => number;
  updateTitle: (id: number, title: string, loading: boolean) => void;
  popTitle: (id: number) => void;
}

const PageTitleContext = createContext<PageTitleContextValue | undefined>(undefined);

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [layers, setLayers] = useState<TitleLayer[]>([]);
  const idSeq = useRef(0);

  const pushTitle = useCallback((title: string, loading: boolean) => {
    const id = ++idSeq.current;
    setLayers((prev) => [...prev, { id, title, loading }]);
    return id;
  }, []);

  const updateTitle = useCallback((id: number, title: string, loading: boolean) => {
    setLayers((prev) => prev.map((layer) => (layer.id === id ? { ...layer, title, loading } : layer)));
  }, []);

  const popTitle = useCallback((id: number) => {
    setLayers((prev) => prev.filter((layer) => layer.id !== id));
  }, []);

  const top = layers[layers.length - 1];
  const title = top?.title ?? '';
  const titleLoading = top?.loading ?? false;

  useEffect(() => {
    document.title = titleLoading ? 'BUG DOS' : title || 'BUG DOS';
  }, [title, titleLoading]);

  return (
    <PageTitleContext.Provider value={{ title, titleLoading, pushTitle, updateTitle, popTitle }}>
      {children}
    </PageTitleContext.Provider>
  );
}

export function usePageTitleContext(): PageTitleContextValue {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error('usePageTitleContext must be used within PageTitleProvider');
  }
  return context;
}

export function usePageTitle(title?: string, loading = false): void {
  const { pushTitle, updateTitle, popTitle } = usePageTitleContext();
  const idRef = useRef<number | null>(null);

  useEffect(() => {
    const shouldClaim = title !== undefined || loading;
    if (!shouldClaim) {
      if (idRef.current != null) {
        popTitle(idRef.current);
        idRef.current = null;
      }
      return;
    }

    const nextTitle = title ?? '';
    if (idRef.current == null) {
      idRef.current = pushTitle(nextTitle, loading);
    } else {
      updateTitle(idRef.current, nextTitle, loading);
    }
  }, [title, loading, pushTitle, updateTitle, popTitle]);

  useEffect(() => {
    return () => {
      if (idRef.current != null) {
        popTitle(idRef.current);
        idRef.current = null;
      }
    };
  }, [popTitle]);
}
